import * as THREE from "three";

/**
 * Orange/red emissive torus that orbits the ship's stern when speedBoost
 * is at peak. Off when boost < 0.4; ramps in/out via opacity & scale damp
 * so toggles never read as a hard pop.
 *
 * Like StreakAura, lives in scene-space and follows ship.position each
 * frame — keeps the trauma shake from double-perturbing the visual.
 */
export class BoostRing {
  readonly mesh: THREE.Mesh;
  private mat: THREE.MeshBasicMaterial;
  private spin = 0;

  constructor() {
    const geo = new THREE.TorusGeometry(0.72, 0.06, 8, 32);
    this.mat = new THREE.MeshBasicMaterial({
      color: 0xff7733,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.rotation.x = Math.PI / 2;
  }

  /**
   * Per-frame update. boost is 0..1 ship.speedBoost.
   */
  update(dt: number, shipPos: THREE.Vector3, boost: number) {
    this.mesh.position.copy(shipPos);
    this.mesh.position.z += 0.85; // behind the glow disc
    // Under reduced-motion, hold the spin steady — the ring still shows
    // (it's a visual presence cue) but doesn't rotate.
    const reduced = typeof document !== "undefined" &&
      document.body.classList.contains("reduced-motion");
    if (!reduced) {
      this.spin += dt * (2 + boost * 6);
    }
    this.mesh.rotation.z = this.spin;
    const target = boost > 0.4 ? 0.65 * boost : 0;
    const k = 1 - Math.pow(0.001, dt);
    this.mat.opacity = THREE.MathUtils.lerp(this.mat.opacity, target, k);
    // Color shifts orange → near-white as boost saturates.
    const t = Math.max(0, Math.min(1, (boost - 0.4) / 0.6));
    const r = 1;
    const g = 0.46 + 0.5 * t;
    const b = 0.2 + 0.6 * t;
    this.mat.color.setRGB(r, g, b);
    const s = 1 + boost * 0.35;
    this.mesh.scale.setScalar(THREE.MathUtils.lerp(this.mesh.scale.x, s, k));
  }
}
